import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import Image from '@components/Image';
import { Photo, SortTypes, WithClassName } from '@types';
import GalleryFilters, { ColorFilter } from './GalleryFilters';
import GalleryLayout from './GalleryLayout';
import GallerySlider from './GallerySlider';
import classNames from 'classnames';

interface GalleryProps {
  colorFilter?: ColorFilter;
  filters?: ReactNode;
  onSlideChange: (id?: string) => void;
  photos: Photo[];
  sortType?: SortTypes;
  subTitle?: ReactNode;
  title: ReactNode;
}

const Gallery = ({
  className,
  colorFilter,
  filters,
  onSlideChange,
  photos,
  sortType = 'date_desc',
  subTitle,
  title,
}: GalleryProps & WithClassName) => {
  const { photoId } = useParams();

  const filteredPhotos = colorFilter
    ? photos.filter(({ color }) => {
        if (colorFilter === 'b&w') return !color;
        else if (colorFilter === 'color') return !!color;
        return true;
      })
    : photos;

  const sortedPhotos = filteredPhotos.sort((a, b) => {
    if (sortType === 'date_asc') {
      return a.date !== b.date
        ? a.date.localeCompare(b.date)
        : a.id.localeCompare(b.id);
    } else {
      return b.date !== a.date
        ? b.date.localeCompare(a.date)
        : b.id.localeCompare(a.id);
    }
  });

  const [slideId, setSlideId] = useState<string>(photoId);
  const slideIndex = slideId
    ? sortedPhotos.findIndex(({ id }) => slideId === id)
    : undefined;

  useEffect(() => {
    setSlideId(photoId);
  }, [photoId]);

  return (
    <>
      <div className={className}>
        {title}
        {(subTitle || filters) && (
          <div
            className={classNames(
              'flex tablet:items-center',
              'max-tablet:flex-col-reverse max-tablet:gap-8',
              'tablet:h-5',
              subTitle ? 'mb-4 tablet:mb-8' : 'mb-8 tablet:mb-8',
            )}
          >
            {subTitle && <div className="shrink-0 text-nowrap">{subTitle}</div>}
            {filters && <div className="grow">{filters}</div>}
          </div>
        )}

        <GalleryLayout
          elements={sortedPhotos}
          renderElement={(photo: Photo) => (
            <div
              className="cursor-pointer"
              onClick={() => {
                setSlideId(photo.id);
                onSlideChange(photo.id);
              }}
            >
              <Image photo={photo} thumbnail />
            </div>
          )}
        />
      </div>

      {!!slideId && slideIndex !== undefined && slideIndex > -1 && (
        <GallerySlider
          initialSlide={slideIndex}
          photos={sortedPhotos}
          onClose={() => {
            setSlideId(undefined);
            onSlideChange(undefined);
          }}
          onSlideChange={(id: number) => {
            const photo = sortedPhotos[id];
            onSlideChange(photo.id);
          }}
        />
      )}
    </>
  );
};

export default Gallery;
